import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiTrash, HiEye } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import './ContactRequestIndex.scss';

interface ContactRequest {
    id: number;
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
    isRead: boolean;
    createdAt: string;
}

export const ContactRequestIndex: React.FC = () => {
    const { t, i18n } = useTranslation(['contactRequests', 'common']);
    const isRTL = i18n.language === 'ar';

    const [requests, setRequests] = useState<ContactRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState<Record<string, string>>({});

    const buildQuery = (page: number, size: number) => {
        const params = new URLSearchParams({
            page: page.toString(),
            pageSize: size.toString()
        });

        Object.entries(filters).forEach(([key, value]) => {
            const trimmed = value.trim();
            if (!trimmed) return;
            params.append(key, trimmed);
        });

        return params.toString();
    };

    const fetchRequests = async (page: number, size: number) => {
        try {
            setLoading(true);
            const query = buildQuery(page, size);
            const response = await adminApi.call(`/ContactRequest?${query}`, 'GET');

            if (!response.success) {
                showErrorToast(response.error || t('contactRequests:messages.fetchError'));
                return;
            }

            setRequests(response.data?.items || []);
            setTotalItems(response.data?.totalItems || 0);
        } catch (error) {
            showErrorToast(t('contactRequests:messages.fetchError'));
            console.error('Error fetching contact requests:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests(currentPage, pageSize);
    }, [currentPage, pageSize, filters]);

    const deleteRequest = async (id: number) => {
        if (!window.confirm(t('contactRequests:messages.deleteConfirm'))) {
            return;
        }

        try {
            const response = await adminApi.call(`/ContactRequest/${id}`, 'DELETE');

            if (!response.success) {
                showErrorToast(response.error || t('contactRequests:messages.deleteError'));
                return;
            }

            showSuccessToast(t('contactRequests:messages.deleteSuccess'));
            fetchRequests(currentPage, pageSize);
        } catch (error) {
            showErrorToast(t('contactRequests:messages.deleteError'));
            console.error('Error deleting contact request:', error);
        }
    };

    const viewRequest = async (id: number) => {
        try {
            const response = await adminApi.call(`/ContactRequest/${id}`, 'GET');
            if (response.success) {
                // Refresh list to update read status
                fetchRequests(currentPage, pageSize);
                // Show in a modal or separate page? For now just alert or log
                // Ideally we'd have a modal here.
                alert(`Subject: ${response.data.subject}\n\nFrom: ${response.data.name} (${response.data.email})\n\nMessage:\n${response.data.message}`);
            }
        } catch (error) {
            console.error('Error viewing request:', error);
        }
    };

    const columns: Column[] = [
        {
            key: 'name',
            title: t('contactRequests:table.name'),
            sortable: true,
            filterable: true,
            filterKey: 'name',
        },
        {
            key: 'email',
            title: t('contactRequests:table.email'),
            sortable: true,
            filterable: true,
            filterKey: 'email',
        },
        {
            key: 'subject',
            title: t('contactRequests:table.subject'),
            sortable: true,
        },
        {
            key: 'message',
            title: t('contactRequests:table.message'),
            render: (value) => <div className="message-preview" title={value}>{value}</div>
        },
        {
            key: 'status',
            title: t('contactRequests:table.status'),
            render: (_, row) => (
                row.isRead ?
                    <span className="text-gray-400">{t('contactRequests:status.read')}</span> :
                    <span className="unread-badge">{t('contactRequests:status.unread')}</span>
            )
        },
        {
            key: 'createdAt',
            title: t('contactRequests:table.createdAt'),
            sortable: true,
            render: (value) => new Date(value).toLocaleString(),
        },
        {
            key: 'actions',
            title: t('contactRequests:table.actions'),
            sortable: false,
            width: '120px',
            align: 'center',
            render: (_, row) => (
                <div className="action-buttons">
                    <ActionButton
                        variant="primary"
                        size="sm"
                        icon={HiEye}
                        onClick={() => viewRequest(row.id)}
                        tooltip={t('contactRequests:view')}
                    />
                    {hasPermission("Delete Contact Request") && (
                        <ActionButton
                            variant="danger"
                            size="sm"
                            icon={HiTrash}
                            onClick={() => deleteRequest(row.id)}
                            tooltip={t('contactRequests:delete')}
                        />
                    )}
                </div>
            ),
        },
    ];

    const breadcrumbs = [
        { label: t('contactRequests:breadcrumbs.contactRequests') }
    ];

    return (
        <PageLayout
            title={t('contactRequests:title')}
            subtitle={t('contactRequests:subtitle')}
            breadcrumbs={breadcrumbs}
            className={`${isRTL ? 'rtl' : 'ltr'} contact-request-index`}
            isIndexPage={true}
        >
            <DataTable
                columns={columns}
                data={requests}
                loading={loading}
                searchable={true}
                filterable={true}
                refreshable={true}
                paginationMode="server"
                currentPage={currentPage}
                totalItems={totalItems}
                pageSize={pageSize}
                pageSizeOptions={[10, 20, 50]}
                showPageSizeSelector={true}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                }}
                onFilterChange={(key, value) => {
                    setFilters(prev => ({ ...prev, [key]: value }));
                    setCurrentPage(1);
                }}
                onRefresh={() => fetchRequests(currentPage, pageSize)}
                emptyMessage={t('contactRequests:messages.noRequests')}
            />
        </PageLayout>
    );
};
